import React from 'react';
import { HowItWorks } from '../components/HowItWorks';
import { BenefitCards } from '../components/BenefitCards';
import { WhyPay } from '../components/WhyPay';
import { ArrowRight, Globe, FileText, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage, Language } from '../contexts/LanguageContext';
type Card = {
  title: string;
  body: string;
};
type S = {
  expectHeading: string;
  expectSub: string;
  cards: Card[];
  ctaHeading: string;
  ctaBody: string;
  ctaBtn: string;
};
// EN/ES finalized. HT/VI = best effort, pending native review.
const T: Record<Language, S> = {
  en: {
    expectHeading: 'What you can expect along the way',
    expectSub: 'Small details that make a real difference for our customers.',
    cards: [{
      title: 'In Your Language',
      body: 'Switch between English, Spanish, Haitian Creole, and Vietnamese at any time. Translations stay clear and consistent.'
    }, {
      title: 'Free Digital Delivery',
      body: 'Your completed PDF arrives by email at no extra cost. Print it at home, or visit any local print shop.'
    }, {
      title: 'Optional Printed Delivery',
      body: 'Prefer paper? Add our mailed printed packet for a small fee — shipped neatly with your submission checklist.'
    }],
    ctaHeading: 'Ready to get started?',
    ctaBody: 'Begin your guided application in your preferred language. No account required to start.',
    ctaBtn: 'See pricing'
  },
  es: {
    expectHeading: 'Qué puede esperar en el camino',
    expectSub: 'Pequeños detalles que hacen una verdadera diferencia para nuestros clientes.',
    cards: [{
      title: 'En su idioma',
      body: 'Cambie entre inglés, español, creole haitiano y vietnamita cuando quiera. Las traducciones se mantienen claras y consistentes.'
    }, {
      title: 'Entrega digital gratis',
      body: 'Su PDF completo le llega por correo sin costo adicional. Imprímalo en casa o vaya a cualquier tienda de impresión.'
    }, {
      title: 'Entrega impresa opcional',
      body: '¿Prefiere papel? Agregue nuestro paquete impreso por correo por una pequeña tarifa — enviado ordenado con su lista para entregar.'
    }],
    ctaHeading: '¿Listo para comenzar?',
    ctaBody: 'Comience su solicitud guiada en el idioma que prefiera. No necesita cuenta para empezar.',
    ctaBtn: 'Ver precios'
  },
  ht: {
    expectHeading: 'Sa ou ka atàn sou wout la',
    expectSub: 'Ti detay ki fè yon vrè diferans pou kliyan nou yo.',
    cards: [{
      title: 'Nan lang ou',
      body: 'Chanje ant anglè, panyòl, kreyòl ayisyen, ak vyetnamyen nenpòt lè. Tradiksyon yo rete klè e konsistan.'
    }, {
      title: 'Livrezon dijital gratis',
      body: 'PDF konplè ou an rive pa imèl san okenn frè anplüs. Enprime li lakay ou, oswa ale nan nenpòt magazen enprime.'
    }, {
      title: 'Livrezon enprime opsyonèl',
      body: 'Ou pi renmen papye? Ajoute pakè enprime nou an pa lapòs pou yon ti frè — voye pwòp ak lis pou soumèt ou an.'
    }],
    ctaHeading: 'Pare pou kòmanse?',
    ctaBody: 'Kòmanse aplikasyon gide ou an nan lang ou pi pito. Ou pa bezwen kont pou kòmanse.',
    ctaBtn: 'Gade pri yo'
  },
  vi: {
    expectHeading: 'Những gì quý vị có thể mong đợi trên hành trình',
    expectSub: 'Những chi tiết nhỏ tạo nên khác biệt thực sự cho khách hàng của chúng tôi.',
    cards: [{
      title: 'Bằng ngôn ngữ của quý vị',
      body: 'Chuyển đổi giữa tiếng Anh, Tây Ban Nha, Creole Haiti và tiếng Việt bất cứ lúc nào. Bản dịch luôn rõ ràng và nhất quán.'
    }, {
      title: 'Giao bản kỹ thuật số miễn phí',
      body: 'Tệp PDF hoàn chỉnh của quý vị được gửi qua email mà không tốn thêm phí. In tại nhà, hoặc đến bất kỳ tiệm in nào.'
    }, {
      title: 'Tùy chọn giao bản in',
      body: 'Thích bản giấy? Thêm bộ hồ sơ in gửi qua bưu điện với một khoản phí nhỏ — được gửi gọn gàng kèm danh sách nộp đơn của quý vị.'
    }],
    ctaHeading: 'Sẵn sàng bắt đầu?',
    ctaBody: 'Bắt đầu đơn có hướng dẫn của quý vị bằng ngôn ngữ ưu tiên. Không cần tài khoản để bắt đầu.',
    ctaBtn: 'Xem bảng giá'
  }
};

// Lands directly on the steps ("How the process works"), which is what visitors
// expect when they click "How it works." No separate (repeated) page title.
export function HowItWorksPage() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  const icons = [<Globe className="w-6 h-6 text-brand-accent" data-id="element-590" data-mp="30c2uj7bqsix5" />, <FileText className="w-6 h-6 text-brand-accent" data-id="element-591" data-mp="2vhymslunk6v0" />, <ShieldCheck className="w-6 h-6 text-brand-accent" data-id="element-592" data-mp="2qjx3q0x887jj" />];
  return <div data-id="element-593" data-mp="2qjncyb4cxwcs">
      {/* Main steps — the on-topic opener */}
      <HowItWorks data-id="element-594" data-mp="3nzkon9ihbt3a" />

      {/* Why pay if it's free — the honest differentiator */}
      <WhyPay data-id="element-595" data-mp="3j8ui3sybhk70" />

      {/* Detail callouts */}
      <section className="py-24 bg-white border-t border-brand-border" data-id="element-596" data-mp="3j71fqpuqlm4t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-597" data-mp="3ria4081pl4af">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-598" data-mp="3vitwuzf91lqr">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-textPrimary mb-4" data-id="element-599" data-mp="3vj3pyvvlc90o">
              {t.expectHeading}
            </h2>
            <p className="text-lg text-brand-textSecondary" data-id="element-600" data-mp="9bc8wulm69z2">{t.expectSub}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-601" data-mp="eaot17l3p2xy">
            {t.cards.map((item, i) => <div key={i} className="bg-brand-bg/40 rounded-2xl p-8 border border-brand-border" data-id="element-602" data-mp="icacxcw1yhhn" data-mpi={i}>
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm border border-brand-border mb-6" data-id="element-603" data-mp="n4bm2pxxbs3g">
                  {icons[i]}
                </div>
                <h3 className="text-xl font-bold text-brand-textPrimary mb-3" data-id="element-604" data-mp="x29abdfs9ow2">
                  {item.title}
                </h3>
                <p className="text-brand-textSecondary leading-relaxed" data-id="element-605" data-mp="120j3xjlv0hfk">
                  {item.body}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      <BenefitCards data-id="element-606" data-mp="7evotmblfy6g" />

      {/* CTA */}
      <section className="py-24 bg-brand-bg" data-id="element-607" data-mp="m1oyoc2cgbr9">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-608" data-mp="m3i2ljlkohvk">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-textPrimary mb-6" data-id="element-609" data-mp="ha75ia6kbfgu">
            {t.ctaHeading}
          </h2>
          <p className="text-lg text-brand-textSecondary mb-10" data-id="element-610" data-mp="v0sgzdtosn4p">{t.ctaBody}</p>
          <Link to="/pricing" className="inline-flex items-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 focus:ring-offset-brand-bg group" data-id="element-611" data-mp="q2qwcogm4fr4">
            {t.ctaBtn}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-612" data-mp="31u4kz0kd35rw" />
          </Link>
        </div>
      </section>
    </div>;
}